import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "./useTenant";
import { toast } from "sonner";

export interface CustodianConnection {
  id: string;
  org_id: string;
  family_id: string | null;
  provider: string;
  credentials_token: string;
  bank_id: string | null;
  bank_name: string | null;
  bank_logo_url: string | null;
  status: string;
  sca_token: string | null;
  last_error: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  sync_count: number;
  connected_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustodianSyncLog {
  id: string;
  connection_id: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  portfolios_synced: number;
  positions_synced: number;
  transactions_synced: number;
  error_message: string | null;
}

export function useCustodianConnections() {
  const { orgId, familyId } = useTenant();
  const queryClient = useQueryClient();

  // Query all connections for the org
  const {
    data: connections,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["custodian-connections", orgId],
    queryFn: async () => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from("custodian_connections")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as CustodianConnection[];
    },
    enabled: !!orgId,
  });

  // Initiate bank connection (requires entityId)
  const connect = useMutation({
    mutationFn: async (entityId: string) => {
      if (!entityId) {
        throw new Error("Please select an entity before connecting a bank.");
      }

      const { data, error } = await supabase.functions.invoke("flanks-link-init", {
        body: { orgId, familyId, entityId },
      });

      if (error) throw error;
      if (!data?.linkUrl) throw new Error("Failed to get connection URL");

      // Redirect to Flanks Link
      window.location.href = data.linkUrl;
    },
    onError: (err) => {
      toast.error("Failed to initiate bank connection", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  // Handle callback from Flanks (call this after redirect back)
  const handleCallback = useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      const { data, error } = await supabase.functions.invoke("flanks-link-callback", {
        body: { code, state },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Connected to ${data.bankName}`, {
        description: "Your bank account has been linked. Syncing data...",
      });
      queryClient.invalidateQueries({ queryKey: ["custodian-connections", orgId] });

      // Trigger initial sync
      syncNow.mutate(undefined);
    },
    onError: (err) => {
      toast.error("Failed to complete bank connection", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  // Disconnect a bank
  const disconnect = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase
        .from("custodian_connections")
        .update({ status: "disconnected" })
        .eq("id", connectionId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Bank disconnected");
      queryClient.invalidateQueries({ queryKey: ["custodian-connections", orgId] });
    },
    onError: (err) => {
      toast.error("Failed to disconnect bank", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  // Trigger manual sync
  const syncNow = useMutation({
    mutationFn: async (connectionId?: string) => {
      const { data, error } = await supabase.functions.invoke("sync-flanks-data", {
        body: connectionId ? { connectionId } : {},
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const results = data.results || [];
      const successful = results.filter((r: { status: string }) => r.status === "success").length;
      const failed = results.filter((r: { status: string }) => r.status === "failed").length;

      if (successful > 0 && failed === 0) {
        toast.success("Sync completed", {
          description: `Successfully synced ${successful} connection(s)`,
        });
      } else if (successful > 0 && failed > 0) {
        toast.warning("Sync partially completed", {
          description: `${successful} succeeded, ${failed} failed`,
        });
      } else if (failed > 0) {
        toast.error("Sync failed", {
          description: results.find((r: { status: string }) => r.status === "failed")?.error || "Unknown error",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["custodian-connections", orgId] });
      queryClient.invalidateQueries({ queryKey: ["holdings"] });
      queryClient.invalidateQueries({ queryKey: ["positions"] });
    },
    onError: (err) => {
      toast.error("Sync failed", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  // Re-authenticate for SCA
  const reAuthenticate = useMutation({
    mutationFn: async (connectionId: string) => {
      const connection = connections?.find((c) => c.id === connectionId);
      if (!connection?.sca_token) {
        throw new Error("No SCA token available");
      }

      // Redirect to Flanks Link with SCA token
      const scaUrl = `https://link.flanks.io/?sca_token=${connection.sca_token}&bank=${connection.bank_id}`;
      window.location.href = scaUrl;
    },
    onError: (err) => {
      toast.error("Failed to initiate re-authentication", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  // Get sync logs for a connection
  const getSyncLogs = async (connectionId: string): Promise<CustodianSyncLog[]> => {
    const { data, error } = await supabase
      .from("custodian_sync_log")
      .select("*")
      .eq("connection_id", connectionId)
      .order("started_at", { ascending: false })
      .limit(10);

    if (error) throw error;
    return (data || []) as CustodianSyncLog[];
  };

  return {
    connections,
    isLoading,
    error,
    refetch,
    connect,
    handleCallback,
    disconnect,
    syncNow,
    reAuthenticate,
    getSyncLogs,
  };
}
